import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast, Card } from "@jsq/ds/core";
import { FormCheckbox, SubmitButton } from "@jsq/ds/forms";
import { useAutomatedPaymentsReconciliationGapsLazyQuery } from "~/graphql/supergraphHooks";
import {
  type AutomatedPaymentsReconciliationGapsQuery,
  type AutomatedPaymentsReconciliationGapsInput,
} from "~/graphql/supergraphTypes";
import { getErrorMessage } from "~/utils/errors";
import { toDbId } from "~/utils/graphql-helpers";
import * as styles from "./styles_vanilla.css";

// Types
type ReconciliationGapType =
  AutomatedPaymentsReconciliationGapsQuery["automatedPaymentsReconciliationGaps"][0];

interface FormValues {
  hasApprovedFundedPayment: boolean;
}

const initialValues: FormValues = {
  hasApprovedFundedPayment: true,
};

const validationSchema = Yup.object().shape({
  hasApprovedFundedPayment: Yup.boolean().required(),
});

const ReconciliationGaps: React.FC = () => {
  const [submittedData, setSubmittedData] = useState<ReconciliationGapType[] | null>(null);

  const [fetchReconciliationGaps, { loading }] =
    useAutomatedPaymentsReconciliationGapsLazyQuery({
      onError: (error) => {
        toast.error(getErrorMessage(error));
      },
    });

  const handleSubmit = async (values: FormValues) => {
    const input: AutomatedPaymentsReconciliationGapsInput = {
      hasApprovedFundedPayment: values.hasApprovedFundedPayment,
    };

    const result = await fetchReconciliationGaps({
      variables: { input },
    });

    if (result.data?.automatedPaymentsReconciliationGaps) {
      setSubmittedData(result.data.automatedPaymentsReconciliationGaps);
      toast.success(
        `Found ${result.data.automatedPaymentsReconciliationGaps.length} reconciliation gap(s)`,
      );
    }
  };

  return (
    <Flex
      data-testid="reconciliation-gaps-root"
      flexDirection="column"
      className={styles.container}
    >
      <Flex flexDirection="column" className={`inset-lg ${styles.headerSection}`}>
        <Text variant="regular-xxxl">Reconciliation Gaps</Text>
      </Flex>
      <div className={styles.contentArea} data-testid="reconciliation-gaps-content">
        <Card className="stacked-lg">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <Flex flexDirection="column" className="stacked-md">
                <FormCheckbox
                  name="hasApprovedFundedPayment"
                  label="Include Approved Funded Payments"
                />
                <Text variant="regular-sm" className="inset-lg-x">
                  Filter for payments with approved risk review status
                </Text>
              </Flex>
              <Flex justifyContent="flex-start" className="stacked-lg">
                <SubmitButton disabled={loading}>
                  {loading ? "Querying..." : "Query Reconciliation Gaps"}
                </SubmitButton>
              </Flex>
            </Form>
          </Formik>
        </Card>
        {submittedData && submittedData.length > 0 && (
          <Card>
            <Text variant="regular-xl" marginBottom="lg">
              Query Results ({submittedData.length} gap
              {submittedData.length !== 1 ? "s" : ""})
            </Text>
            <Flex flexDirection="column" className="stacked-md">
              {submittedData.map((gap, index) => (
                <Card key={index} className={styles.resultCard}>
                  <Flex flexDirection="column" className="stacked-md">
                    <div className={styles.cardHeader}>
                      <Text variant="regular-lg" className={styles.cardTitle}>
                        Reconciliation Gap #{index + 1}
                      </Text>
                    </div>

                    {/* Expected Payment Provider Object */}
                    <div className={styles.dataSection}>
                      <Text
                        variant="regular-md"
                        className={styles.cardSubtitle}
                        marginBottom="sm"
                      >
                        Expected Payment Provider Object
                      </Text>
                      <Flex flexDirection="column" className="stacked-xs">
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.expectedPaymentProviderObject.id}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Provider ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.expectedPaymentProviderObject.providerId}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Arena ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.expectedPaymentProviderObject.arenaId}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Object ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.expectedPaymentProviderObject.objectId}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Object Type:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.expectedPaymentProviderObject.objectType}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Provider Object ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.expectedPaymentProviderObject.providerObjectId}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Provider Object Type:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.expectedPaymentProviderObject.providerObjectType}
                          </Text>
                        </Flex>
                        {gap.expectedPaymentProviderObject.refProviderObjectId && (
                          <Flex justifyContent="space-between">
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Ref Provider Object ID:
                            </Text>
                            <Text variant="regular-sm">
                              {gap.expectedPaymentProviderObject.refProviderObjectId}
                            </Text>
                          </Flex>
                        )}
                        {gap.expectedPaymentProviderObject.refProviderObjectType && (
                          <Flex justifyContent="space-between">
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Ref Provider Object Type:
                            </Text>
                            <Text variant="regular-sm">
                              {gap.expectedPaymentProviderObject.refProviderObjectType}
                            </Text>
                          </Flex>
                        )}
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Timestamp:
                          </Text>
                          <Text variant="regular-sm">
                            {new Date(
                              gap.expectedPaymentProviderObject.timestamp,
                            ).toLocaleString()}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Created At:
                          </Text>
                          <Text variant="regular-sm">
                            {new Date(
                              gap.expectedPaymentProviderObject.createdAt,
                            ).toLocaleString()}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Updated At:
                          </Text>
                          <Text variant="regular-sm">
                            {new Date(
                              gap.expectedPaymentProviderObject.updatedAt,
                            ).toLocaleString()}
                          </Text>
                        </Flex>
                      </Flex>
                    </div>

                    {/* Incoming Payment Detail Provider Object */}
                    <div className={styles.dataSection}>
                      <Text
                        variant="regular-md"
                        className={styles.cardSubtitle}
                        marginBottom="sm"
                      >
                        Incoming Payment Detail Provider Object
                      </Text>
                      <Flex flexDirection="column" className="stacked-xs">
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.incomingPaymentDetailProviderObject.id}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Provider ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.incomingPaymentDetailProviderObject.providerId}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Arena ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.incomingPaymentDetailProviderObject.arenaId}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Object ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.incomingPaymentDetailProviderObject.objectId}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Object Type:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.incomingPaymentDetailProviderObject.objectType}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Provider Object ID:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.incomingPaymentDetailProviderObject.providerObjectId}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Provider Object Type:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.incomingPaymentDetailProviderObject.providerObjectType}
                          </Text>
                        </Flex>
                        {gap.incomingPaymentDetailProviderObject.refProviderObjectId && (
                          <Flex justifyContent="space-between">
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Ref Provider Object ID:
                            </Text>
                            <Text variant="regular-sm">
                              {gap.incomingPaymentDetailProviderObject.refProviderObjectId}
                            </Text>
                          </Flex>
                        )}
                        {gap.incomingPaymentDetailProviderObject.refProviderObjectType && (
                          <Flex justifyContent="space-between">
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Ref Provider Object Type:
                            </Text>
                            <Text variant="regular-sm">
                              {gap.incomingPaymentDetailProviderObject.refProviderObjectType}
                            </Text>
                          </Flex>
                        )}
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Timestamp:
                          </Text>
                          <Text variant="regular-sm">
                            {new Date(
                              gap.incomingPaymentDetailProviderObject.timestamp,
                            ).toLocaleString()}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Created At:
                          </Text>
                          <Text variant="regular-sm">
                            {new Date(
                              gap.incomingPaymentDetailProviderObject.createdAt,
                            ).toLocaleString()}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Updated At:
                          </Text>
                          <Text variant="regular-sm">
                            {new Date(
                              gap.incomingPaymentDetailProviderObject.updatedAt,
                            ).toLocaleString()}
                          </Text>
                        </Flex>
                      </Flex>
                    </div>

                    {/* Payment Order Provider Objects */}
                    {gap.paymentOrderProviderObjects.length > 0 && (
                      <div className={styles.dataSection}>
                        <Text
                          variant="regular-md"
                          className={styles.cardSubtitle}
                          marginBottom="sm"
                        >
                          Payment Order Provider Objects (
                          {gap.paymentOrderProviderObjects.length})
                        </Text>
                        {gap.paymentOrderProviderObjects.map(
                          (paymentOrder, paymentOrderIndex) => (
                            <div key={paymentOrderIndex} className="stacked-sm">
                              <Text
                                variant="regular-sm"
                                className={styles.dataLabel}
                                marginBottom="xs"
                              >
                                Payment Order #{paymentOrderIndex + 1}
                              </Text>
                              <Flex flexDirection="column" className="stacked-xs">
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    ID:
                                  </Text>
                                  <Text variant="regular-sm">{paymentOrder.id}</Text>
                                </Flex>
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    Provider ID:
                                  </Text>
                                  <Text variant="regular-sm">{paymentOrder.providerId}</Text>
                                </Flex>
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    Arena ID:
                                  </Text>
                                  <Text variant="regular-sm">{paymentOrder.arenaId}</Text>
                                </Flex>
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    Object ID:
                                  </Text>
                                  <Text variant="regular-sm">{paymentOrder.objectId}</Text>
                                </Flex>
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    Object Type:
                                  </Text>
                                  <Text variant="regular-sm">{paymentOrder.objectType}</Text>
                                </Flex>
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    Provider Object ID:
                                  </Text>
                                  <Text variant="regular-sm">
                                    {paymentOrder.providerObjectId}
                                  </Text>
                                </Flex>
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    Provider Object Type:
                                  </Text>
                                  <Text variant="regular-sm">
                                    {paymentOrder.providerObjectType}
                                  </Text>
                                </Flex>
                                {paymentOrder.refProviderObjectId && (
                                  <Flex justifyContent="space-between">
                                    <Text variant="regular-sm" className={styles.dataLabel}>
                                      Ref Provider Object ID:
                                    </Text>
                                    <Text variant="regular-sm">
                                      {paymentOrder.refProviderObjectId}
                                    </Text>
                                  </Flex>
                                )}
                                {paymentOrder.refProviderObjectType && (
                                  <Flex justifyContent="space-between">
                                    <Text variant="regular-sm" className={styles.dataLabel}>
                                      Ref Provider Object Type:
                                    </Text>
                                    <Text variant="regular-sm">
                                      {paymentOrder.refProviderObjectType}
                                    </Text>
                                  </Flex>
                                )}
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    Timestamp:
                                  </Text>
                                  <Text variant="regular-sm">
                                    {new Date(paymentOrder.timestamp).toLocaleString()}
                                  </Text>
                                </Flex>
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    Created At:
                                  </Text>
                                  <Text variant="regular-sm">
                                    {new Date(paymentOrder.createdAt).toLocaleString()}
                                  </Text>
                                </Flex>
                                <Flex justifyContent="space-between">
                                  <Text variant="regular-sm" className={styles.dataLabel}>
                                    Updated At:
                                  </Text>
                                  <Text variant="regular-sm">
                                    {new Date(paymentOrder.updatedAt).toLocaleString()}
                                  </Text>
                                </Flex>
                              </Flex>
                            </div>
                          ),
                        )}
                      </div>
                    )}

                    {/* Funding Payment */}
                    <div className={styles.dataSection}>
                      <Text
                        variant="regular-md"
                        className={styles.cardSubtitle}
                        marginBottom="sm"
                      >
                        Funding Payment
                      </Text>
                      <Flex flexDirection="column" className="stacked-xs">
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            ID:
                          </Text>
                          <Text variant="regular-sm">{gap.fundingPayment.id}</Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Object ID:
                          </Text>
                          <Text variant="regular-sm">
                            {toDbId(gap.fundingPayment.objectId)}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Object Type:
                          </Text>
                          <Text variant="regular-sm">{gap.fundingPayment.objectType}</Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Status:
                          </Text>
                          <Text variant="regular-sm">{gap.fundingPayment.status}</Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Amount:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.fundingPayment.amount.currency}{" "}
                            {gap.fundingPayment.amount.amount}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Method:
                          </Text>
                          <Text variant="regular-sm">{gap.fundingPayment.method}</Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Direction:
                          </Text>
                          <Text variant="regular-sm">{gap.fundingPayment.direction}</Text>
                        </Flex>
                        {gap.fundingPayment.bankAccountId && (
                          <Flex
                            justifyContent="space-between"
                            className={styles.compactDataRow}
                          >
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Bank Account ID:
                            </Text>
                            <Text variant="regular-sm">{gap.fundingPayment.bankAccountId}</Text>
                          </Flex>
                        )}
                        {gap.fundingPayment.transmittedAt && (
                          <Flex
                            justifyContent="space-between"
                            className={styles.compactDataRow}
                          >
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Transmitted At:
                            </Text>
                            <Text variant="regular-sm">
                              {new Date(gap.fundingPayment.transmittedAt).toLocaleString()}
                            </Text>
                          </Flex>
                        )}
                        {gap.fundingPayment.settledAt && (
                          <Flex
                            justifyContent="space-between"
                            className={styles.compactDataRow}
                          >
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Settled At:
                            </Text>
                            <Text variant="regular-sm">
                              {new Date(gap.fundingPayment.settledAt).toLocaleString()}
                            </Text>
                          </Flex>
                        )}
                        {gap.fundingPayment.failedAt && (
                          <Flex
                            justifyContent="space-between"
                            className={styles.compactDataRow}
                          >
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Failed At:
                            </Text>
                            <Text variant="regular-sm">
                              {new Date(gap.fundingPayment.failedAt).toLocaleString()}
                            </Text>
                          </Flex>
                        )}
                        {gap.fundingPayment.scheduledProcessDate && (
                          <Flex
                            justifyContent="space-between"
                            className={styles.compactDataRow}
                          >
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Scheduled Process Date:
                            </Text>
                            <Text variant="regular-sm">
                              {new Date(
                                gap.fundingPayment.scheduledProcessDate,
                              ).toLocaleString()}
                            </Text>
                          </Flex>
                        )}
                      </Flex>
                    </div>

                    {/* Payment Batch */}
                    <div className={styles.dataSection}>
                      <Text
                        variant="regular-md"
                        className={styles.cardSubtitle}
                        marginBottom="sm"
                      >
                        Payment Batch
                      </Text>
                      <Flex flexDirection="column" className="stacked-xs">
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            ID:
                          </Text>
                          <Text variant="regular-sm">{gap.paymentBatch.id}</Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Object ID:
                          </Text>
                          <Text variant="regular-sm">{toDbId(gap.paymentBatch.objectId)}</Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Status:
                          </Text>
                          <Text variant="regular-sm">{gap.paymentBatch.status}</Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Net Amount:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.paymentBatch.netAmount.currency}{" "}
                            {gap.paymentBatch.netAmount.amount}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Objects Count:
                          </Text>
                          <Text variant="regular-sm">{gap.paymentBatch.objectsCount}</Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Required Approval Count:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.paymentBatch.requiredApprovalCount}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Different Approver Required:
                          </Text>
                          <Text variant="regular-sm">
                            {gap.paymentBatch.isDifferentApproverRequired ? "Yes" : "No"}
                          </Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Creator ID:
                          </Text>
                          <Text variant="regular-sm">{gap.paymentBatch.creatorId}</Text>
                        </Flex>
                        <Flex justifyContent="space-between" className={styles.compactDataRow}>
                          <Text variant="regular-sm" className={styles.dataLabel}>
                            Created At:
                          </Text>
                          <Text variant="regular-sm">
                            {new Date(gap.paymentBatch.createdAt).toLocaleString()}
                          </Text>
                        </Flex>
                        {gap.paymentBatch.canceledAt && (
                          <Flex
                            justifyContent="space-between"
                            className={styles.compactDataRow}
                          >
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Canceled At:
                            </Text>
                            <Text variant="regular-sm">
                              {new Date(gap.paymentBatch.canceledAt).toLocaleString()}
                            </Text>
                          </Flex>
                        )}
                        {gap.paymentBatch.canceledByUserId && (
                          <Flex
                            justifyContent="space-between"
                            className={styles.compactDataRow}
                          >
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Canceled By User ID:
                            </Text>
                            <Text variant="regular-sm">
                              {gap.paymentBatch.canceledByUserId}
                            </Text>
                          </Flex>
                        )}
                        {gap.paymentBatch.paymentDescription && (
                          <Flex
                            justifyContent="space-between"
                            className={styles.compactDataRow}
                          >
                            <Text variant="regular-sm" className={styles.dataLabel}>
                              Payment Description:
                            </Text>
                            <Text variant="regular-sm">
                              {gap.paymentBatch.paymentDescription}
                            </Text>
                          </Flex>
                        )}
                      </Flex>
                    </div>
                  </Flex>
                </Card>
              ))}
            </Flex>
          </Card>
        )}
        {submittedData && submittedData.length === 0 && (
          <Card>
            <div className={styles.noResultsSection}>
              <Text variant="regular-lg" marginBottom="sm">
                No Results Found
              </Text>
              <Text variant="regular-md">
                No reconciliation gaps were found with the current filter settings.
              </Text>
            </div>
          </Card>
        )}
      </div>
    </Flex>
  );
};

export default ReconciliationGaps;
export { ReconciliationGaps };
